import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import sprite from '../../assets/icons/sprite.svg';
import { OrganisationTag } from '../../services';
import {
  CardOpenButton,
  ConfirmationModal,
  CookieActionType,
  cookieOptions,
  getCssVar,
  isAdminLayout,
  layoutPath,
  useKeyDown,
  useOnClickOutside,
  useSearchByTags,
} from '../../shared';
import { IconLabel } from '../../shared/components/IconLabel';

import classes from './SearchInput.module.scss';

const SEARCH_HISTORY_COOKIE_KEY = 'searchHistory';

const MAX_PINNED_ALLOWED = 3;

interface SearchHistoryItem {
  value: string;
  id?: number;
  isPinned?: boolean;
}

interface SearchInputProps {
  autoFocus?: boolean;
}

export const SearchInput: FunctionComponent<SearchInputProps> = ({ autoFocus }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();

  const { tagsSearchParams, tagsToSearchBy, isTagSelected, searchByTagIdRemoval } =
    useSearchByTags();

  const qs = searchParams.get('qs') ?? '';

  const [cookies, setCookie, removeCookie] = useCookies([SEARCH_HISTORY_COOKIE_KEY]);

  const historyItems: SearchHistoryItem[] = useMemo(
    () => cookies[SEARCH_HISTORY_COOKIE_KEY]?.items ?? [],
    [cookies]
  );

  const [value, setValue] = useState('');

  const [showItemsList, setShowItemsList] = useState(false);

  const [showTags, setShowTags] = useState(false);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const searchLabel = t('common.search');

  const searchPagePath = useMemo(() => layoutPath('/search'), []);

  const pinAllowed = useMemo(
    () => historyItems.filter(({ isPinned }) => Boolean(isPinned)).length < MAX_PINNED_ALLOWED,
    [historyItems]
  );

  const isTagString = useCallback((value: string) => value.startsWith('#'), []);

  useOnClickOutside(containerRef, () => setShowItemsList(false));

  const setHistoryItems = useCallback(
    (items: SearchHistoryItem[]) => {
      setCookie(SEARCH_HISTORY_COOKIE_KEY, { items }, cookieOptions());
    },
    [setCookie]
  );

  useEffect(() => {
    if (pathname !== searchPagePath) {
      setValue('');
    }
  }, [pathname, searchPagePath]);

  const clear = useCallback(() => {
    setValue('');
    inputRef.current?.focus();
  }, []);

  const setHistoryItemsHandler = useCallback(
    ({ value, id }: SearchHistoryItem) => {
      const itemIndex = historyItems.findIndex((historyItem) => historyItem.value === value);

      if (itemIndex !== -1 && historyItems.length === 1) {
        return;
      }

      const items = [
        itemIndex !== -1 && historyItems.length
          ? historyItems.splice(itemIndex, 1)[0]
          : { value, id },
        ...historyItems,
      ];

      setHistoryItems(items);
    },
    [historyItems, setHistoryItems]
  );

  const search = useCallback(
    (value: string) => {
      inputRef.current?.blur();

      setShowItemsList(false);

      if (value) {
        setHistoryItemsHandler({ value });
      }

      navigate({
        pathname: searchPagePath,
        search: `?${createSearchParams({ qs: value, tags: tagsSearchParams })}`,
      });
    },
    [navigate, searchPagePath, setHistoryItemsHandler, tagsSearchParams]
  );

  const searchByTag = useCallback(
    (tag: OrganisationTag) => {
      const { id, name } = tag;

      if (!showTags && isTagSelected(tag)) {
        setShowItemsList(false);
        return;
      }

      if (isTagSelected(tag)) {
        searchByTagIdRemoval(id);
        return;
      }

      setHistoryItemsHandler({ id, value: `#${name}` });

      navigate({
        pathname: searchPagePath,
        search: `?${createSearchParams({
          qs,
          tags: tagsSearchParams ? `${tagsSearchParams}_${id}` : `${id}`,
        })}`,
      });
    },
    [
      isTagSelected,
      navigate,
      qs,
      searchByTagIdRemoval,
      searchPagePath,
      setHistoryItemsHandler,
      showTags,
      tagsSearchParams,
    ]
  );

  const onKeyDown = useCallback(
    ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
      if (key === 'Enter' && !isTagString(value)) {
        search(value);
      }
    },
    [isTagString, search, value]
  );

  const changeHandler = useCallback(({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setValue(target.value);
    setShowItemsList(true);
  }, []);

  const pinHistoryItem = useCallback(
    (value: string, isPinned: boolean) => {
      const index = historyItems.findIndex((historyItem) => historyItem.value === value);

      if (index === -1) {
        return;
      }

      historyItems[index].isPinned = !isPinned;

      setHistoryItems(historyItems);
    },
    [historyItems, setHistoryItems]
  );

  const pinHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>, value: string, isPinned: boolean) => {
      event.stopPropagation();
      pinHistoryItem(value, isPinned);
    },
    [pinHistoryItem]
  );

  const deleteHistoryItem = useCallback(
    (value: string) => {
      const index = historyItems.findIndex((historyItem) => historyItem.value === value);

      if (index === -1) {
        return;
      }

      historyItems.splice(index, 1);

      setHistoryItems(historyItems);
    },
    [historyItems, setHistoryItems]
  );

  const deleteHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>, value: string) => {
      event.stopPropagation();
      deleteHistoryItem(value);
    },
    [deleteHistoryItem]
  );

  const deleteConfirmationHandler = useCallback(() => {
    removeCookie(SEARCH_HISTORY_COOKIE_KEY, cookieOptions({ action: CookieActionType.REMOVE }));

    setIsDeleteOpen(false);
  }, [removeCookie]);

  const clearHistory = useCallback(() => {
    setShowItemsList(false);
    setIsDeleteOpen(true);
  }, []);

  const closeItemsListHandler = useCallback(() => {
    if (!showItemsList || !containerRef.current?.contains(document.activeElement)) {
      return;
    }

    inputRef.current?.focus();
    setShowItemsList(false);
  }, [showItemsList]);

  useKeyDown({ key: 'Escape', action: closeItemsListHandler });

  useEffect(() => {
    if (showTags && !value.length) {
      setShowTags(false);
    }

    if (!showTags && isTagString(value)) {
      setShowTags(true);
    }
  }, [isTagString, showTags, value]);

  const renderHistory = useMemo(() => {
    const filteredHistoryItems = historyItems.filter((historyItem) =>
      historyItem.value.toLowerCase().startsWith(value.toLowerCase())
    );

    if (!filteredHistoryItems.length) {
      return (
        <div className={classes['search__container-empty']}>{t('search.no-recent-searches')}</div>
      );
    }

    return (
      <>
        <div className={classes['search__container-header']}>
          <div className={classes['search__container-header-title']}>{t('common.recent')}</div>
          <IconLabel
            label={t('common.clear-all')}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={clearHistory}
          />
        </div>
        <div className={classes['search__container-list']}>
          {filteredHistoryItems
            .sort(({ isPinned: isPinnedA }, { isPinned: isPinnedB }) =>
              isPinnedA === isPinnedB ? 0 : isPinnedA ? -1 : 1
            )
            .map(({ value, id, isPinned }: SearchHistoryItem) => {
              return (
                <div
                  key={id ?? value}
                  className={classes['search__container-list-item']}
                  onClick={() =>
                    id && isTagString(value)
                      ? searchByTag({ id, name: value.substring(1) } as OrganisationTag)
                      : search(value)
                  }
                >
                  <CardOpenButton label={value} />

                  <span className={classes['search__container-list-item-value']}>{value}</span>
                  <span className={classes['search__container-list-item-actions']}>
                    {(isPinned || pinAllowed) && (
                      <IconLabel
                        iconId={'pin'}
                        iconSize={20}
                        ariaLabel={t(`common.${isPinned ? 'unpin' : 'pin'}`)}
                        color={isPinned ? getCssVar('--base-link-text-hover-color') : '#888888'}
                        hoverColor={getCssVar('--base-link-text-hover-color')}
                        onClick={(event) => pinHandler(event, value, Boolean(isPinned))}
                      />
                    )}
                    <IconLabel
                      iconId={'delete'}
                      iconSize={20}
                      ariaLabel={t('common.delete')}
                      color={'#888888'}
                      hoverColor={getCssVar('--color-danger')}
                      onClick={(event) => deleteHandler(event, value)}
                    />
                  </span>
                </div>
              );
            })}
        </div>
      </>
    );
  }, [
    clearHistory,
    deleteHandler,
    historyItems,
    isTagString,
    pinAllowed,
    pinHandler,
    search,
    searchByTag,
    t,
    value,
  ]);

  const renderTags = useMemo(() => {
    const filteredTags = tagsToSearchBy.filter((tag) =>
      `#${tag.name.toLowerCase()}`.startsWith(value.toLowerCase())
    );

    if (!filteredTags.length) {
      return <div className={classes['search__container-empty']}>{t('search.no-tags-found')}</div>;
    }

    return (
      <>
        <div className={classes['search__container-header']}>
          <div className={classes['search__container-header-title']}>{t('search.tags')}</div>
        </div>
        <div className={classes['search__container-list']}>
          {filteredTags.map((tag) => {
            const { id, name, type } = tag;

            return (
              <button
                key={id}
                type={'button'}
                className={classes['search__container-list-item']}
                aria-pressed={isTagSelected(tag)}
                onClick={() => searchByTag(tag)}
              >
                <span className={classes['search__container-list-item-value']}>#{name}</span>

                <span className={classes['search__container-list-item-icon-type-wrapper']}>
                  {isTagSelected(tag) && (
                    <svg className={classes['search__container-list-item-icon']}>
                      <use href={`${sprite}#checkmark`} />
                    </svg>
                  )}
                  {isAdminLayout() && (
                    <span className={classes['search__container-list-item-type']}>{`[${t(
                      `organisationTagType.${type}`
                    )}]`}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </>
    );
  }, [isTagSelected, searchByTag, t, tagsToSearchBy, value]);

  const renderItems = useMemo(
    () => (showTags ? renderTags : renderHistory),
    [renderHistory, renderTags, showTags]
  );

  return (
    <div
      ref={containerRef}
      className={classes['search']}
      onBlur={({ relatedTarget }) =>
        Boolean(relatedTarget) &&
        !containerRef.current?.contains(relatedTarget as Node) &&
        setShowItemsList(false)
      }
    >
      <IconLabel
        className={classes['search__icon']}
        iconId={'search'}
        iconSize={20}
        color={getCssVar('--search-input-icon-color')}
        singleColor
        nonClickable
      />

      <input
        ref={inputRef}
        type={'text'}
        className={classes['search__input']}
        placeholder={searchLabel}
        aria-label={searchLabel}
        value={value}
        enterKeyHint={'search'}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        onChange={changeHandler}
        onFocus={() => setShowItemsList(true)}
      />

      {showItemsList && <div className={classes['search__container']}>{renderItems}</div>}

      {value && (
        <IconLabel
          className={classes['search__clear']}
          iconId={'close'}
          ariaLabel={t('common.clear')}
          color={getCssVar('--search-input-close-color')}
          singleColor
          onClick={clear}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        acceptLabel={t('common.delete')}
        onAccept={deleteConfirmationHandler}
        title={t('search.delete-confirmation-title')}
        subTitle={t('search.delete-confirmation-sub-title')}
        danger
      />
    </div>
  );
};
